import { Component, computed, input } from '@angular/core';

/**
 * Avatar ของผู้ใช้ — fallback chain:
 *   1) ถ้ามี avatarUrl → แสดงรูป
 *   2) ถ้าไม่มีรูป แต่ตัวแรกของชื่อเป็นอักษร/ตัวเลข → แสดงตัวอักษรนั้น (uppercase)
 *   3) ถ้าไม่มีชื่อ หรือตัวแรกเป็นเครื่องหมายพิเศษ (เช่น "+66...") → แสดงไอคอนคน
 *
 * เหตุผล: ลูกค้าที่สมัครด้วยเบอร์ displayName มักขึ้นต้นด้วย "+" ทำให้ avatar โชว์ "+" แทนตัวอักษรชื่อ
 */
@Component({
  selector: 'app-user-avatar',
  standalone: true,
  template: `
    <div
      class="rounded-full flex items-center justify-center font-bold shrink-0 overflow-hidden"
      [class.text-white]="!avatarUrl() && variant() === 'accent'"
      [style.width.px]="size()"
      [style.height.px]="size()"
      [style.font-size.px]="fontPx()"
      [style.background]="backgroundStyle()"
      [style.color]="colorStyle()"
    >
      @if (avatarUrl()) {
        <img [src]="avatarUrl()" alt="" class="w-full h-full object-cover">
      } @else if (initial()) {
        {{ initial() }}
      } @else {
        <span class="material-symbols-outlined" [style.font-size.px]="iconPx()">person</span>
      }
    </div>
  `,
})
export class UserAvatar {
  /** ชื่อผู้ใช้ — ใช้สำหรับดึงตัวอักษรแรกเป็น initial */
  readonly name = input<string | null | undefined>(null);

  /** URL รูป avatar (ถ้ามี) — ส่งเป็น full URL พร้อม fileUrl prefix แล้ว */
  readonly avatarUrl = input<string | null | undefined>(null);

  /** ขนาด avatar เป็น pixel (default 32) */
  readonly size = input<number>(32);

  /**
   * Variant สี background เมื่อไม่มีรูป
   *  - 'subtle' (default): bg-surface-hover + text-secondary — ใช้ในรายการ
   *  - 'accent': gradient accent — ใช้กับ winner / highlight
   *  - 'accent-light': accent lighter — ใช้กับ avatar ที่ต้องการเน้นเล็กน้อย
   */
  readonly variant = input<'subtle' | 'accent' | 'accent-light'>('subtle');

  /** ตัวอักษรแรก uppercase — null ถ้าไม่มีชื่อ หรือตัวแรกไม่ใช่ alphanumeric (จะ fallback เป็นไอคอนคน) */
  readonly initial = computed(() => getDisplayInitial(this.name() ?? ''));

  /** font-size ของตัวอักษร — ~45% ของ size */
  readonly fontPx = computed(() => Math.max(10, Math.round(this.size() * 0.45)));

  /** icon size ของ person — ~60% ของ size */
  readonly iconPx = computed(() => Math.max(14, Math.round(this.size() * 0.6)));

  readonly backgroundStyle = computed(() => {
    if (this.avatarUrl()) return '';
    switch (this.variant()) {
      case 'accent': return 'linear-gradient(135deg, var(--color-accent), var(--color-accent-dark))';
      case 'accent-light': return 'var(--color-accent-lighter)';
      default: return 'var(--color-surface-hover, #f3f4f6)';
    }
  });

  readonly colorStyle = computed(() => {
    if (this.avatarUrl()) return '';
    switch (this.variant()) {
      case 'accent': return '#fff';
      case 'accent-light': return 'var(--color-accent-darker)';
      default: return 'var(--color-text-secondary, #6b7280)';
    }
  });
}

/**
 * ดึงตัวอักษรแรกของชื่อ (uppercase) สำหรับใช้เป็น initial avatar
 * คืน null ถ้าไม่มีชื่อ หรือตัวแรกไม่ใช่ตัวอักษร/ตัวเลข (เช่น "+66..." → null)
 */
export function getDisplayInitial(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const first = trimmed.charAt(0);
  // \p{L} = ตัวอักษรทุกภาษา, \p{N} = ตัวเลข — ครอบคลุมไทย, อังกฤษ, ตัวเลข
  return /[\p{L}\p{N}]/u.test(first) ? first.toUpperCase() : null;
}
